// Hands a workout summary (text, and the picture card when there is one) to
// whatever can send it on: the Android app's share sheet, the browser's Web
// Share, the clipboard, or a download, in that order of preference.
//
// Returns what happened so the caller can word its toast:
//   "shared"     the share sheet opened (and wasn't cancelled)
//   "cancelled"  the user closed the share sheet
//   "copied"     put on the clipboard instead
//   "downloaded" the picture was saved instead
//   "update-app" an Android app too old to share pictures
//   "failed"     nothing worked

import { Capacitor, registerPlugin } from "@capacitor/core";
import { isNativeApp } from "./native";
import { copyTextToClipboard } from "../utils/shareLinks";

const KhronosShare = registerPlugin("KhronosShare");

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

// An app built before the share plugin rejects with UNIMPLEMENTED.
const nativeShareAvailable = () => {
  if (!isNativeApp()) return false;
  try {
    return Capacitor.isPluginAvailable("KhronosShare");
  } catch {
    return false;
  }
};

const isAbort = (err) => err?.name === "AbortError";

/** Save a Blob as a file through a temporary link. */
export const downloadBlob = (blob, fileName) => {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return true;
  } catch {
    return false;
  }
};

/**
 * Share a picture (`image`, a PNG Blob) or a message (`text`). Either can be
 * left out; people send one or the other.
 */
export async function shareWorkout({ text = "", image = null, fileName = "khronos-workout.png", title = "Workout" }) {
  if (nativeShareAvailable()) {
    try {
      await KhronosShare.share({
        text,
        title: "Share workout",
        fileName,
        ...(image ? { imageBase64: await blobToBase64(image) } : {}),
      });
      return "shared";
    } catch (err) {
      console.warn("[shareWorkout] native share failed", err);
      // fall through to the web paths
    }
  }

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    const file = image && typeof File === "function" ? new File([image], fileName, { type: "image/png" }) : null;
    const withFile = file ? { files: [file], title, ...(text ? { text } : {}) } : null;
    try {
      if (withFile && (!navigator.canShare || navigator.canShare(withFile))) {
        await navigator.share(withFile);
        return "shared";
      }
      if (!image) {
        await navigator.share({ text, title });
        return "shared";
      }
    } catch (err) {
      if (isAbort(err)) return "cancelled";
      console.warn("[shareWorkout] Web Share failed", err);
    }
  }

  // An app installed before the share plugin can't hand a picture on: the
  // WebView has no Web Share and its downloads go to Chrome, which can't
  // fetch the page's in-memory picture.
  if (image && isNativeApp()) return "update-app";

  // No share sheet that takes a picture: save the picture (and put any text
  // on the clipboard to paste alongside).
  if (image) {
    const saved = downloadBlob(image, fileName);
    if (text) await copyTextToClipboard(text);
    return saved ? "downloaded" : "failed";
  }
  return (await copyTextToClipboard(text)) ? "copied" : "failed";
}
